import { useCallback, useRef, useState } from 'react';
import store from 'Store';
import { completeAuthentication as completeAuthAction } from 'actions/AuthActions';
import { useReturnUrl } from 'pages/Auth/SignIn/hooks/useReturnUrl';
import Url from 'utils/Url';

/**
 * Hook that owns which auth screen the SignIn page is currently showing AND the
 * authentication completion for the new /signin flow (SIM-10 / ACS-4134).
 *
 * State machine (SIM-1): { currentScreen, goTo }. Known screens: 'passkey-detection'
 * (initial), 'credential' (SIM-2), 'authorization'/'otp' (SIM-6..9). goTo(screen)
 * accepts any key so sibling stories add screens without changing the signature.
 *
 * Completion orchestration (SIM-10, ADR-SIM10-002): completeAuthentication dispatches the
 * AuthActions.completeAuthentication DISPATCHER (which routes OTP -> verifyOTP +
 * signInSuccessAfterMFA, password -> signIn), then on success redirects to the validated
 * returnUrl STRICTLY AFTER the session is stored. On failure it never redirects; it routes
 * authError:
 *   - ERR_OTP_LOGIN_SESSION_ERROR / session-timeout -> authError 'sessionExpired',
 *     clear state (goTo 'credential') and redirect to /signin (AC-028)
 *   - any other error (incl. backend 5xx) -> authError 'genericError', no auto-retry,
 *     no redirect (AC-034)
 * The error is rethrown so useSubmitGuard can reset its in-flight state.
 *
 * The dispatch goes through the Store singleton (not useDispatch) because Auth hooks run
 * outside a react-redux Provider in their tests — the established Auth pattern.
 */
const INITIAL_SCREEN = 'passkey-detection';
const SESSION_ERROR_CODE = 'ERR_OTP_LOGIN_SESSION_ERROR';
const SIGNIN_CLEARED_PATH = '/signin';

const useAuthFlow = () => {
    const [currentScreen, setCurrentScreen] = useState(INITIAL_SCREEN);
    const [authError, setAuthError] = useState(null);
    // Additive (SIM-7 / ACS-4131, F-002): the lookup result carried forward by
    // CredentialEntryScreen so AuthorizationScreen/OtpEntryScreen have their inputs
    // without a re-lookup. Existing consumers are unaffected — purely added to the
    // returned object.
    const [authContext, setAuthContext] = useState(null);
    // ADR-SIM8-001: broaden goTo to (screen, contextPatch?). The optional patch is
    // MERGED into authContext BEFORE the screen swap so the incoming screen reads a
    // stable value on its first render (the password → risk-step-up handoff carries
    // mfaMode / phoneLast4Digits / credential this way). Existing single-arg callers
    // (SIM-2 CredentialEntryScreen, SIM-7 AuthorizationScreen) are unaffected — the
    // second arg is optional and the merge is skipped when it's absent.
    const goTo = useCallback((screen, contextPatch) => {
        if (contextPatch) {
            setAuthContext(prev => ({ ...(prev || {}), ...contextPatch }));
        }

        setCurrentScreen(screen);
    }, []);

    // `|| {}` is load-bearing: the SIM-1 HK-1 test path renders this hook without mocking
    // useReturnUrl (auto-mock returns undefined), so guard the destructure. Not dead code.
    const { redirectToReturnUrl } = useReturnUrl() || {};
    // Keep the latest redirect callback without adding it to completeAuthentication's deps.
    const redirectRef = useRef(redirectToReturnUrl);
    redirectRef.current = redirectToReturnUrl;

    // SIM-24 (ACS-4418) code-review #7 / audit F-015: expose a STABLE always-callable wrapper
    // instead of the raw `redirectToReturnUrl` destructured above. The raw value can be undefined
    // when useReturnUrl() returns undefined (test/auto-mock path — see `|| {}` guard). Consumers
    // in SignIn.f.jsx (SINGLE .then, MULTIPLE successCallback) call it as `redirectToReturnUrl()`
    // WITHOUT optional-chain and would throw TypeError under mock. The wrapper reads the ref so
    // it always sees the latest concrete function (including hot-reload / test-swap cases), and
    // is a no-op when the raw value is missing. Stable identity (empty deps) so consumers'
    // useEffect deps are not perturbed.
    const stableRedirectToReturnUrl = useCallback(() => {
        redirectRef.current?.();
    }, []);

    const completeAuthentication = useCallback(async params => {
        setAuthError(null);

        let session;

        try {
            // Dispatcher routes by method and completes token handling internally.
            session = await store.dispatch(completeAuthAction(params));
        } catch (error) {
            if (error && error.errorCode === SESSION_ERROR_CODE) {
                // Session expired/invalid: clear in-flight flow state and bounce to
                // /signin (which re-evaluates the SIM-1 two-layer gate on landing).
                setAuthError('sessionExpired');
                setCurrentScreen('credential');
                Url.redirectTo(SIGNIN_CLEARED_PATH);
            } else {
                // Backend 5xx / generic: show the generic message, no auto-retry, no redirect.
                setAuthError('genericError');
            }

            // Rethrow so useSubmitGuard resets and the caller can react.
            throw error;
        }

        // Redirect to the validated returnUrl STRICTLY AFTER the session is established.
        // Guarded: useReturnUrl() is destructured with `|| {}`, so redirectToReturnUrl can be
        // undefined (e.g. the hook renders outside a mounted useReturnUrl); a missing redirect
        // must not throw and turn a successful auth into a crash. (Supersedes the Copilot
        // Autofix `typeof` guard with equivalent optional chaining.)
        redirectRef.current?.();

        return session;
    }, []);

    return {
        currentScreen,
        goTo,
        completeAuthentication,
        authError,
        authContext,
        setAuthContext,
        // SIM-24 (ACS-4418): expose the passkey-success redirect seam so consumers do not
        // re-mount useReturnUrl (each mount re-reads location.search and re-writes sessionStorage).
        // Exports the STABLE always-callable wrapper (not the raw destructure) so consumers can
        // invoke it without optional-chain and still be safe under test/auto-mock (code-review #7).
        redirectToReturnUrl: stableRedirectToReturnUrl
    };
};

export default useAuthFlow;
